type AsyncFunction = (...args: any[]) => Promise<any>;
type TaskQueue = Array<() => Promise<void>>;

/**
 * 并发限制装饰器, 用于装饰异步函数
 */
export function limitConcurrency<T extends Object>(concurrency: number) {
    return function (
        target: T,
        propertyKey: string,
        descriptor: TypedPropertyDescriptor<AsyncFunction>,
    ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const originalMethod = descriptor.value!;
        let activeTasks = 0;
        const pendingTaskQueue: TaskQueue = [];

        // eslint-disable-next-line no-param-reassign
        descriptor.value = async function (...args: any[]) {
            if (activeTasks < concurrency) {
                try {
                    // 如果当前并发数小于限制，则直接执行
                    activeTasks += 1;
                    const result = await originalMethod.apply(this, args);
                    return result;
                } finally {
                    activeTasks -= 1;
                    processQueue();
                }
            } else {
                // 否则，将任务添加到队列中
                return new Promise((resolve, reject) => {
                    const task = async () => {
                        try {
                            activeTasks += 1;
                            const result = await originalMethod.apply(this, args);
                            resolve(result);
                        } catch (error) {
                            reject(error);
                        } finally {
                            activeTasks -= 1;
                            processQueue();
                        }
                    };

                    pendingTaskQueue.push(task);
                });
            }
        };

        function processQueue(): void {
            if (activeTasks >= concurrency || pendingTaskQueue.length === 0) return;

            const task = pendingTaskQueue.shift();
            if (!task) return processQueue();
            task();
        }
    };
}

/**
 * 并发限制类封装; 相比装饰器实现，可以获取到各种内部状态
 */
export class Task<T extends AsyncFunction> {
    public pendingTaskQueue: TaskQueue = [];
    public activeTasks = 0;

    public execute = (async (...args: any[]) => {
        if (this.activeTasks < this.concurrency) {
            try {
                // 如果当前并发数小于限制，则直接执行
                this.activeTasks += 1;
                const result = await this.method(...args);
                return result;
            } finally {
                this.activeTasks -= 1;
                this.processQueue();
            }
        } else {
            // 否则，将任务添加到队列中
            return new Promise((resolve, reject) => {
                const task = async () => {
                    try {
                        this.activeTasks += 1;
                        const result = await this.method(...args);
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    } finally {
                        this.activeTasks -= 1;
                        this.processQueue();
                    }
                };

                this.pendingTaskQueue.push(task);
            });
        }
    }) as any as T;

    public constructor(private method: T, private concurrency: number) {}

    public async processQueue() {
        if (this.activeTasks >= this.concurrency || this.pendingTaskQueue.length === 0) return;

        const task = this.pendingTaskQueue.shift();
        if (!task) return this.processQueue();
        task();
    }

    public cancelAll() {
        this.pendingTaskQueue.length = 0;
    }
}
